// v2 frog — a true crouch: the body tilted nose-up off propped little front
// arms, a pale throat pouch, a dark dorsal stripe, and the signature eye bumps
// done properly — raised lid domes carrying golden eyes with black pupils —
// plus fully articulated folded hind legs (thigh, shank and a splayed foot in a
// Z-fold) ready to spring. Two skins: leaf green and olive-brown. Ported 1:1
// from age-of-mcp's v2/frog.ts.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const SKINS = [
  { back: "#4f9e3f", side: "#60ae4c", belly: "#cfe8ae", leg: "#3f8634", eye: "#e8c83c", stripe: "#2e6428" },
  { back: "#7c7c3c", side: "#8c8c4a", belly: "#d8d2a6", leg: "#62622e", eye: "#d8b83c", stripe: "#4a4a20" },
];

function frogParts(v: number): Part[] {
  const c = SKINS[v % SKINS.length]!;
  const parts: Part[] = [
    // body tilted nose-up in the crouch
    { geometry: new THREE.IcosahedronGeometry(0.023, 0), color: c.back,
      position: [0.002, 0.02, 0], scale: [1.35, 0.85, 1.1], rotation: [0, 0, 0.22] },
    // pale throat pouch
    { geometry: new THREE.IcosahedronGeometry(0.0165, 0), color: c.belly,
      position: [0.016, 0.012, 0], scale: [1.1, 0.7, 1.0] },
    // dark dorsal stripe down the back
    { geometry: new THREE.BoxGeometry(0.045, 0.003, 0.005), color: c.stripe,
      position: [-0.002, 0.0345, 0], rotation: [0, 0, 0.22] },
    // eye bumps: raised lid domes, golden eyes, black pupils
    { geometry: new THREE.IcosahedronGeometry(0.0075, 0), color: c.side,
      position: [0.018, 0.036, 0.0115] },
    { geometry: new THREE.IcosahedronGeometry(0.0075, 0), color: c.side,
      position: [0.018, 0.036, -0.0115] },
    { geometry: new THREE.IcosahedronGeometry(0.005, 0), color: c.eye,
      position: [0.0205, 0.0395, 0.0115] },
    { geometry: new THREE.IcosahedronGeometry(0.005, 0), color: c.eye,
      position: [0.0205, 0.0395, -0.0115] },
    { geometry: new THREE.IcosahedronGeometry(0.0026, 0), color: "#0c0c0a",
      position: [0.024, 0.041, 0.0115] },
    { geometry: new THREE.IcosahedronGeometry(0.0026, 0), color: "#0c0c0a",
      position: [0.024, 0.041, -0.0115] },
    // little front arms propping the chest up
    { geometry: new THREE.CylinderGeometry(0.0034, 0.0028, 0.02, 4), color: c.leg,
      position: [0.021, 0.01, 0.0125], rotation: [0.18, 0, -0.12] },
    { geometry: new THREE.CylinderGeometry(0.0034, 0.0028, 0.02, 4), color: c.leg,
      position: [0.021, 0.01, -0.0125], rotation: [-0.18, 0, -0.12] },
  ];
  // Folded hind legs, one Z-fold per side: thigh forward, shank folded
  // back, splayed foot reaching ahead under the body.
  for (const s of [1, -1]) {
    parts.push(
      { geometry: new THREE.IcosahedronGeometry(0.0115, 0), color: c.leg,
        position: [-0.017, 0.0155, 0.019 * s], scale: [1.45, 0.8, 0.9],
        rotation: [0, 0.5 * s, 0] },
      { geometry: new THREE.IcosahedronGeometry(0.0075, 0), color: c.back,
        position: [-0.029, 0.0095, 0.0245 * s], scale: [1.5, 0.6, 0.75],
        rotation: [0, -0.55 * s, 0] },
      { geometry: new THREE.BoxGeometry(0.02, 0.0042, 0.0095), color: c.leg,
        position: [-0.012, 0.0021, 0.0275 * s], rotation: [0, -0.2 * s, 0] },
    );
  }
  return parts;
}

export const frogV2 = defineModel({
  id: "frog-v2",
  batchId: "fable",
  name: "Frog (v2)",
  tags: ["creature","frog","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly frog.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.05,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => frogParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => frogParts(1) },
  ],
  animations: [],
});
